/** What this filing covers of its group, and the control that changes it - **ADR-0224**.
 *
 * Shown on the filing's Disclosures tab, beside the notice that names the companies left out, and
 * only when there is something to choose: an included subsidiary the figures leave out, or a choice
 * already made. A filing with no companies under it says nothing, as it always did.
 *
 * Four rules, each from a place this product has been wrong before:
 *
 * - **The server's reason reaches the choice.** A choice the filing cannot follow (a regime with no
 *   parent-level provision, a period on equity share where no ownership share is recorded) is not a
 *   greyed button: pressing Save says why beside the choice (Q-06).
 * - **A role is shown what it may do** (Q-05). Everyone reads the choice; an Admin or an Approver
 *   changes it; anybody else is told who can, and is not offered a control that would 403.
 * - **A stale form is a conflict, not a silent overwrite** (Q-12). The edition the form was opened
 *   against is sent; another number comes back as a 409 and the page re-reads.
 * - **Changing an issued filing is a restatement and the screen says so before it is done.** The
 *   issued record stays as filed and stops matching today's figures; the person ticks that they
 *   know, and the same box is what the server asks for.
 *
 * Nothing here states a regulation: the words are the product's, and the explanation sits behind the
 * info control (`filings.boundary-choice`).
 */

import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  CONSOLIDATIONS,
  consolidationLabel,
  isConsolidation,
  useChooseConsolidation,
} from "../api/filingBoundary";
import type { Consolidation } from "../api/filingBoundary";
import { useArtifacts } from "../api/queries";
import type { Report } from "../api/queries";
import { FieldError } from "../components/FieldError";
import { WriteFailed } from "../components/Loaded";
import { NotYourRole } from "../components/NotYourRole";
import { InfoPopover } from "../components/ui/InfoPopover";
import { useFocusRefusal } from "../components/useFocusRefusal";
import { useFormChecks } from "../components/useFormChecks";
import { mayDo } from "../shell/whoCan";
import own from "./ReportsBoundary.module.css";
import notices from "./Reports.module.css";
import styles from "./Screen.module.css";
import { cls } from "./reportsFormat";
import { hasBoundaryChoice } from "./reportsModel";

export function FilingBoundary({ report, role }: { report: Report; role: string | undefined }) {
  const context = report.context;
  const queryClient = useQueryClient();
  const issued = useArtifacts(context.obligation_id);
  // **Unread is not "none issued".** A failed read leaves the count unknown, and the box is then
  // asked for: a restatement confirmed needlessly costs a tick, one missed costs a silent one.
  const issuedKnownNone = issued.isSuccess && issued.data.length === 0;
  const chosen: Consolidation = isConsolidation(context.consolidation_chosen)
    ? context.consolidation_chosen
    : "own_sites";
  const [picked, setPicked] = useState<Consolidation>(chosen);
  const [confirmed, setConfirmed] = useState(false);
  const [refusal, setRefusal] = useState<unknown>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const choose = useChooseConsolidation(context.obligation_id);
  const [checks, attach] = useFormChecks();
  useFocusRefusal(checks.element, refusal);

  if (!hasBoundaryChoice(report)) return null;

  const applied = consolidationLabel(context.consolidation);
  const unavailable = context.consolidation_unavailable;
  const differs = context.consolidation !== context.consolidation_chosen;

  if (!mayDo(role, "chooseFilingBoundary")) {
    return (
      <p className={cls(notices.notice)} data-testid="filing-boundary" data-mode="read">
        <span className={cls(notices.noticeTitle)}>
          Covers: {applied} <InfoPopover infoKey="filings.boundary-choice" />
        </span>
        <NotYourRole action="chooseFilingBoundary" inline testId="filing-boundary-role" />
      </p>
    );
  }

  function save() {
    const ok = checks.run([
      {
        field: "consolidation",
        ok: !(picked === "subsidiaries" && unavailable),
        message: `Subsidiaries cannot be included in this filing: ${unavailable ?? ""}.`,
      },
      {
        field: "consolidation",
        ok: picked !== chosen,
        message: "That is already how this filing is set.",
      },
      {
        field: "confirm",
        ok: issuedKnownNone || confirmed,
        message:
          "Tick to confirm. The issued filing stays as filed and stops matching today's figures.",
      },
    ]);
    if (!ok) return;
    setRefusal(null);
    setSaved(null);
    void choose
      .mutateAsync({
        consolidation: picked,
        expectedEdition: context.consolidation_edition,
        acknowledgeIssued: !issuedKnownNone && confirmed,
      })
      .then(
        (result) => {
          setConfirmed(false);
          setSaved(
            result.invalidated_approvals > 0
              ? `Saved. ${result.invalidated_approvals} sign-off${result.invalidated_approvals === 1 ? "" : "s"} withdrawn.`
              : "Saved.",
          );
        },
        (error: unknown) => setRefusal(error),
      );
  }

  return (
    <form
      ref={attach}
      className={cls(notices.notice, own.form)}
      noValidate
      data-testid="filing-boundary"
      data-mode="edit"
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      <fieldset className={own.fieldset}>
        <legend className={own.legend}>
          What this filing covers <InfoPopover infoKey="filings.boundary-choice" />
        </legend>
        {CONSOLIDATIONS.map((option, index) => (
          <label key={option.value} className={own.option}>
            <input
              type="radio"
              name={`filing-boundary-${context.obligation_id}`}
              value={option.value}
              checked={picked === option.value}
              {...(index === 0 ? checks.field("consolidation") : {})}
              data-testid={`filing-boundary-${option.value}`}
              onChange={() => {
                checks.touch("consolidation");
                setSaved(null);
                setPicked(option.value);
              }}
            />
            {option.label}
          </label>
        ))}
        <FieldError {...checks.say("consolidation")} />
      </fieldset>
      {differs ? (
        <p className={own.settled} data-testid="filing-boundary-differs">
          Chosen: {consolidationLabel(context.consolidation_chosen)}. The figures cover: {applied}
          {unavailable ? ` (${unavailable})` : ""}.
        </p>
      ) : null}
      {issuedKnownNone ? null : (
        <label className={own.confirm}>
          <input
            type="checkbox"
            {...checks.field("confirm")}
            checked={confirmed}
            data-testid="filing-boundary-confirm"
            onChange={(event) => {
              checks.touch("confirm");
              setConfirmed(event.target.checked);
            }}
          />
          <span>
            I know an issued filing will stay as filed and stop matching today&rsquo;s figures.
          </span>
        </label>
      )}
      <FieldError {...checks.say("confirm")} />
      <div className={own.row}>
        <button
          type="submit"
          className={styles.primary}
          data-testid="filing-boundary-save"
          disabled={choose.isPending}
        >
          {choose.isPending ? "Saving…" : "Save"}
        </button>
        {saved ? (
          <span role="status" data-testid="filing-boundary-saved">
            {saved}
          </span>
        ) : null}
      </div>
      {refusal ? (
        <WriteFailed
          error={refusal}
          of="this filing's boundary"
          onReload={() => void queryClient.invalidateQueries()}
          testId="filing-boundary-refused"
          className={own.refusal}
        />
      ) : null}
    </form>
  );
}
